import React, { useEffect, useState, useContext, useRef, useCallback } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import GroupIcon from "@material-ui/icons/Group";
import PermIdentityIcon from '@material-ui/icons/PermIdentity';
import PersonIcon from "@material-ui/icons/Person";
import CreateIcon from '@material-ui/icons/Create';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import BlockIcon from '@material-ui/icons/Block';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import Avatar from '@material-ui/core/Avatar';
import formatSerializedId from '../../utils/formatSerializedId';
import { resolveContactAvatar, hasCustomAvatar } from "../../utils/avatar";
import { i18n } from "../../translate/i18n";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import MarkdownWrapper from "../MarkdownWrapper";
import {
	Badge,
	CardHeader,
	Switch,
	Tooltip,
	Tabs,
	Tab,
	Box,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	ListItemIcon,
	ListItemSecondaryAction,
	CircularProgress,
	Grid,
	Chip,
	TextField,
	InputAdornment,
	Collapse,
	Dialog,
	DialogContent,
	useMediaQuery
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { ContactForm } from "../ContactForm";
import ContactModal from "../ContactModal";
import { ContactNotes } from "../ContactNotes";
import ImageIcon from '@material-ui/icons/Image';
import VideocamIcon from '@material-ui/icons/Videocam';
import AudiotrackIcon from '@material-ui/icons/Audiotrack';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import LinkIcon from '@material-ui/icons/Link';
import InfoIcon from '@material-ui/icons/Info';

import { AuthContext } from "../../context/Auth/AuthContext";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import { TagsKanbanContainer } from "../TagsKanbanContainer";
import NewTicketModal from "../NewTicketModal";
import ChatIcon from '@material-ui/icons/Chat';
import { useHistory } from "react-router-dom";
import { MF_PENDING_TICKET_MESSAGE_SCROLL_KEY } from "../../constants/pendingTicketMessageScroll";
import ShowTicketOpenModal from "../ShowTicketOpenModal";
import GetAppIcon from '@material-ui/icons/GetApp';
import * as XLSX from "xlsx";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
		zIndex: "auto", // Z-index menor que modais mas maior que conteúdo normal
	},
	tabChip: {
		minHeight: 16,
		height: 16,
		fontSize: "0.7rem",
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		marginLeft: 4,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		flexDirection: "column",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
		height: "100%",
		overflow: "hidden", // Importante para evitar overflow no drawer principal
		[theme.breakpoints.down('sm')]: {
			width: "100%", // No mobile, usar largura total
			borderRadius: 0, // Sem bordas arredondadas no mobile
		},
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.inputBackground,
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "50px",
		justifyContent: "flex-start",
		flexShrink: 0,
		position: "relative",
		zIndex: 1,
	},
	// headerTitle: {
	// 	flexGrow: 1,
	// 	textAlign: "center",
	// 	color: theme.palette.text.primary,
	// 	fontWeight: 600,
	// 	whiteSpace: "nowrap",
	// 	overflow: "hidden",
	// 	textOverflow: "ellipsis",
	// },
	profileSection: {
		flexShrink: 0,
		backgroundColor: theme.palette.inputBackground,
	},
	searchContainer: {
		padding: theme.spacing(1, 2),
		backgroundColor: theme.palette.background.paper,
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		flexShrink: 0,
	},
	searchField: {
		"& .MuiOutlinedInput-root": {
			height: 40,
		}
	},
	searchResults: {
		maxHeight: 200,
		overflow: "auto",
		...theme.scrollbarStyles,
	},
	searchResultItem: {
		padding: theme.spacing(1),
		cursor: "pointer",
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
	},
	searchResultText: {
		fontSize: "0.85rem",
		"& mark": {
			backgroundColor: theme.palette.primary.light,
			color: theme.palette.primary.contrastText,
			padding: "0 2px",
			borderRadius: 2,
		}
	},
	searchResultDate: {
		fontSize: "0.75rem",
		color: theme.palette.text.secondary,
		marginTop: 4,
	},
	emptySearchState: {
		textAlign: "center",
		padding: theme.spacing(2),
		color: theme.palette.text.secondary,
		fontSize: "0.85rem",
	},
	tabsContainer: {
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: theme.palette.background.paper,
		flexShrink: 0,
	},
	contentWrapper: {
		display: "flex",
		flexDirection: "column",
		height: "calc(100% - 50px)",
		overflow: "hidden",
	},
	scrollableContent: {
		flex: 1,
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		position: "relative",
		zIndex: 1,
		pointerEvents: "auto", // Garantir que eventos funcionem
	},
	tabPanel: {
		flex: 1,
		overflow: "auto",
		padding: theme.spacing(1),
		...theme.scrollbarStyles,
		// Garantir que cada tab panel tenha rolagem independente
		height: "100%",
		maxHeight: "100%",
		// Melhorar scroll no mobile
		WebkitOverflowScrolling: "touch",
		// Garantir que o conteúdo seja visível
		minHeight: 0,
	},
	// Avatar redondo e menor
	contactAvatar: {
		width: 80,
		height: 80,
		borderRadius: "50%",
		margin: theme.spacing(1),
		border: `2px solid ${theme.palette.primary.main}`,
		cursor: "pointer", // Adicionar cursor pointer
		"&:hover": {
			opacity: 0.8,
		},
	},
	contactHeader: {
		display: "flex",
		padding: theme.spacing(1),
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		"& > *": {
			margin: theme.spacing(0.5),
		},
	},
	contactDetails: {
		marginTop: 8,
		padding: 8,
		display: "flex",
		flexDirection: "column",
		position: "relative",
		zIndex: 1,
		// Garantir que eventos funcionem no mobile
		pointerEvents: "auto",
	},
	contactExtraInfo: {
		marginTop: 4,
		padding: 6,
	},
	switchContainer: {
		padding: theme.spacing(1, 2),
		backgroundColor: theme.palette.background.paper,
		marginBottom: theme.spacing(1),
		flexShrink: 0,
	},
	mediaGrid: {
		padding: theme.spacing(1),
	},
	mediaItem: {
		cursor: "pointer",
		transition: "transform 0.2s",
		"&:hover": {
			transform: "scale(1.05)",
		},
		borderRadius: theme.spacing(1),
		overflow: "hidden",
		height: 100,
		backgroundColor: theme.palette.action.hover,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	mediaThumbnail: {
		width: "100%",
		height: "100%",
		objectFit: "cover",
	},
	mediaIcon: {
		fontSize: 40,
		color: theme.palette.text.secondary,
	},
	loadingContainer: {
		display: "flex",
		justifyContent: "center",
		padding: theme.spacing(3),
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(3),
		color: theme.palette.text.secondary,
	},
	linkItem: {
		padding: theme.spacing(1),
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
		borderRadius: theme.spacing(0.5),
		marginBottom: theme.spacing(0.5),
	},
	// Novos estilos para os ícones de ação
	contactActions: {
		display: "flex",
		flexDirection: "row",
		gap: theme.spacing(1),
		marginTop: theme.spacing(1),
		justifyContent: "center",
	},
	actionIcon: {
		backgroundColor: theme.palette.background.paper,
		border: `1px solid ${theme.palette.divider}`,
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	editIcon: {
		color: theme.palette.primary.main,
	},
	blockIcon: {
		color: theme.palette.error.main,
	},
	calendarIcon: {
		color: theme.palette.info.main,
	},
	unblockIcon: {
		color: theme.palette.success.main,
	},
	tabIcon: {
		minHeight: 48,
		minWidth: 'auto',
		padding: theme.spacing(0.5, 1),
	},
	// Garantir que o CardHeader não cause overflow
	contactCardHeader: {
		width: '100%',
		padding: theme.spacing(1),
	},
	participantsList: {
		padding: 0,
	},
	participantItem: {
		paddingLeft: theme.spacing(1),
		paddingRight: theme.spacing(1),
		marginBottom: theme.spacing(0.5),
		borderRadius: theme.spacing(1),
		"&:hover": {
			backgroundColor: theme.palette.action.hover,
		},
	},
	participantAvatar: {
		width: 45,
		height: 45,
	},
	adminIcon: {
		color: theme.palette.warning.main,
		fontSize: 16,
		backgroundColor: theme.palette.background.paper,
		borderRadius: "50%",
		padding: 2,
	},
	superAdminIcon: {
		color: theme.palette.error.main,
		fontSize: 16,
		backgroundColor: theme.palette.background.paper,
		borderRadius: "50%",
		padding: 2,
	},
	adminChip: {
		backgroundColor: theme.palette.warning.light,
		color: theme.palette.warning.contrastText,
		fontSize: "0.7rem",
	},
	superAdminChip: {
		backgroundColor: theme.palette.error.light,
		color: theme.palette.error.contrastText,
		fontSize: "0.7rem",
	},
	// Estilos para o modal da imagem
	imageModal: {
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	imageModalContent: {
		outline: "none",
		maxWidth: "90vw",
		maxHeight: "90vh",
	},
	expandedImage: {
		width: "100%",
		height: "auto",
		maxWidth: "500px",
		borderRadius: theme.spacing(1),
	},
	exportButton: {
		marginLeft: theme.spacing(2),
		textTransform: "none",
	},
	exportButtonContainer: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: theme.spacing(2),
	}
}));

function TabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`contact-tabpanel-${index}`}
			aria-labelledby={`contact-tab-${index}`}
			style={{
				height: "100%",
				display: value === index ? "flex" : "none",
				flexDirection: "column",
				overflow: "hidden",
				position: "relative",
				zIndex: 1
			}}
			{...other}
		>
			{value === index && (
				<div 
					className={props.classes?.tabPanel} 
					style={{ 
						flex: 1, 
						overflow: "auto", 
						WebkitOverflowScrolling: "touch",
						position: 'relative',
						zIndex: 1,
						pointerEvents: 'auto'
					}}
					onClick={(e) => {
						// Permitir que eventos passem, mas não bloquear
						e.stopPropagation();
					}}
				>
					{children}
				</div>
			)}
		</div>
	);
}

const ContactDrawer = ({ open, handleDrawerClose, contact, ticket, loading }) => {
	const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();

	const [, setBlockingContact] = useState(contact.active);
	const [openForm, setOpenForm] = useState(false);
	const [contactModalOpen, setContactModalOpen] = useState(false);
	const [tabValue, setTabValue] = useState(0);
	const [mediaData, setMediaData] = useState({ images: [], videos: [], audios: [], documents: [], links: [] });
	const [loadingMedia, setLoadingMedia] = useState(false);
	const { get } = useCompanySettings();
	const [hideNum, setHideNum] = useState(false);
	const { user } = useContext(AuthContext);
	const [acceptAudioMessage, setAcceptAudio] = useState(contact.acceptAudioMessage);
	const [imageModalOpen, setImageModalOpen] = useState(false); // Estado para o modal da imagem
	const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
	const [participantInitialContact, setParticipantInitialContact] = useState(undefined);
	const [openTicketAlert, setOpenTicketAlert] = useState(false);
	const [openTicketInfo, setOpenTicketInfo] = useState({ user: "", queue: "" });


	const [participants, setParticipants] = useState([]);
	const [loadingParticipants, setLoadingParticipants] = useState(false);

	// Modal de mensagens agendadas do contato
	const [schedulesDialogOpen, setSchedulesDialogOpen] = useState(false);
	const [contactSchedules, setContactSchedules] = useState([]);
	const [loadingSchedules, setLoadingSchedules] = useState(false);
	const [schedulesTab, setSchedulesTab] = useState(0); // 0 = Agendadas, 1 = Enviadas
	const [contactScheduleCount, setContactScheduleCount] = useState(0); // total para o balão no ícone

	// Estados para pesquisa
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [showSearchResults, setShowSearchResults] = useState(false);
	const searchTimeoutRef = useRef(null);

	const fetchGroupParticipants = useCallback(async () => {
		if (!contact?.isGroup || !contact?.id) return;

		setLoadingParticipants(true);
		try {
			const params = {};
			if (ticket?.whatsappId) {
				params.whatsappId = ticket.whatsappId;
			}
			const { data } = await api.get(`/contacts/${contact.id}/participants`, { params });
			setParticipants(Array.isArray(data) ? data : []);
		} catch (err) {
			console.error("Erro ao buscar participantes do grupo:", err);
			const msg =
				err?.response?.data?.message ||
				err?.response?.data?.error ||
				"Erro ao carregar participantes do grupo";
			toastError(msg);
			setParticipants([]);
		} finally {
			setLoadingParticipants(false);
		}
	}, [contact?.id, contact?.isGroup, contact?.channel, ticket?.whatsappId, ticket?.channel]);

	useEffect(() => {
		async function fetchData() {
			const lgpdHideNumber = await get({
				"column": "lgpdHideNumber"
			});

			if (lgpdHideNumber === "enabled") setHideNum(true);
		}
		fetchData();
	}, [get]);

	useEffect(() => {
		setAcceptAudio(contact.acceptAudioMessage);
		setOpenForm(false);
		setTabValue(0);
		// Limpar pesquisa ao trocar de contato
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		setParticipants([]); // Limpar participantes

		// Limpar timeout ao trocar de contato
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
			searchTimeoutRef.current = null;
		}

		if (open && contact.id) {
			fetchMediaData();
			if (contact.isGroup) {
				fetchGroupParticipants();
			} else {
				setParticipants([]);
			}
			fetchScheduleCount();
		} else {
			setContactScheduleCount(0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, contact?.id, contact?.isGroup, fetchGroupParticipants]);

	// Limpar timeout quando o componente desmontar
	useEffect(() => {
		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current);
			}
		};
	}, []);

	// Função para abrir modal da imagem
	const handleImageClick = () => {
		if (contact && hasCustomAvatar(contact)) {
			setImageModalOpen(true);
		}
	};

	// Função para fechar modal da imagem
	const handleImageModalClose = () => {
		setImageModalOpen(false);
	};

	// Função para buscar mensagens
	const searchMessages = async (searchParam) => {
		if (!contact?.id) {
			console.warn("Contato não disponível para pesquisa");
			return;
		}

		if (!searchParam || searchParam.trim().length < 2) {
			setSearchResults([]);
			setShowSearchResults(false);
			return;
		}

		setSearchLoading(true);
		try {
			const { data } = await api.get(`/contacts/${contact.id}/messages/search`, {
				params: { searchParam: searchParam.trim() }
			});

			setSearchResults(data.messages || []);
			setShowSearchResults(true);
		} catch (err) {
			console.error("Erro ao buscar mensagens:", err);
			toastError(err);
			setSearchResults([]);
		} finally {
			setSearchLoading(false);
		}
	};

	// Handler para mudança no campo de pesquisa com debounce
	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchTerm(value);

		// Clear timeout anterior
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		// Definir novo timeout para debounce
		searchTimeoutRef.current = setTimeout(() => {
			searchMessages(value);
		}, 500);
	};

	// Buscar apenas a contagem de agendamentos (para o balão no ícone)
	const fetchScheduleCount = async () => {
		if (!contact?.id) return;
		try {
			const { data } = await api.get("/schedules", {
				params: { contactId: contact.id, pageNumber: 1 }
			});
			setContactScheduleCount(data.count ?? 0);
		} catch (err) {
			setContactScheduleCount(0);
		}
	};

	// Buscar agendamentos do contato
	const fetchContactSchedules = async () => {
		if (!contact?.id) return;
		setLoadingSchedules(true);
		try {
			const { data } = await api.get("/schedules", {
				params: { contactId: contact.id }
			});
			setContactSchedules(data.schedules || []);
			setContactScheduleCount(data.count ?? (data.schedules?.length ?? 0));
		} catch (err) {
			console.error("Erro ao carregar agendamentos:", err);
			toastError(err);
			setContactSchedules([]);
		} finally {
			setLoadingSchedules(false);
		}
	};

	const handleOpenSchedulesDialog = () => {
		setSchedulesDialogOpen(true);
		setSchedulesTab(0);
		fetchContactSchedules();
	};

	// Limpar pesquisa
	const clearSearch = () => {
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
			searchTimeoutRef.current = null;
		}
	};

	// Destacar texto na pesquisa
	const highlightSearchTerm = (text) => {
		if (!searchTerm || !text) return text;

		const regex = new RegExp(`(${searchTerm})`, 'gi');
		return text.replace(regex, '<mark>$1</mark>');
	};

	const handleGoToMessage = (message) => {
		const targetTicketId = message.ticketId ?? message.ticket?.id;
		const targetTicketUuid = message.ticket?.uuid;

		if (targetTicketId == null || message.id == null) {
			toast.warning(i18n.t("contactDrawer.searchScrollWrongTicket"));
			return;
		}

		const sameTicket =
			ticket?.id != null && String(targetTicketId) === String(ticket.id);

		if (sameTicket) {
			window.dispatchEvent(
				new CustomEvent("mf-scroll-ticket-message", {
					detail: { messageId: message.id, ticketId: ticket.id },
				})
			);
			setShowSearchResults(false);
			return;
		}

		if (!targetTicketUuid || String(targetTicketUuid).trim() === "") {
			toast.warning(i18n.t("contactDrawer.searchScrollNoUuid"));
			return;
		}

		try {
			sessionStorage.setItem(
				MF_PENDING_TICKET_MESSAGE_SCROLL_KEY,
				JSON.stringify({
					messageId: message.id,
					ticketDbId: Number(targetTicketId),
				})
			);
		} catch (_) {
			toast.warning(i18n.t("contactDrawer.searchScrollStorageError"));
			return;
		}

		history.push(`/tickets/${targetTicketUuid}`);
		setShowSearchResults(false);
	};

	const fetchMediaData = async () => {
		setLoadingMedia(true);
		try {
			const { data } = await api.get(`/contacts/${contact.id}/media`);
			// Garantir que as URLs das mídias estejam corretas
			const processedData = {
				images: data.images.map(item => ({
					...item,
					mediaUrl: item.mediaUrl && !item.mediaUrl.startsWith('http')
						? `${process.env.REACT_APP_BACKEND_URL}${item.mediaUrl}`
						: item.mediaUrl
				})),
				videos: data.videos.map(item => ({
					...item,
					mediaUrl: item.mediaUrl && !item.mediaUrl.startsWith('http')
						? `${process.env.REACT_APP_BACKEND_URL}${item.mediaUrl}`
						: item.mediaUrl
				})),
				audios: data.audios.map(item => ({
					...item,
					mediaUrl: item.mediaUrl && !item.mediaUrl.startsWith('http')
						? `${process.env.REACT_APP_BACKEND_URL}${item.mediaUrl}`
						: item.mediaUrl
				})),
				documents: data.documents.map(item => ({
					...item,
					mediaUrl: item.mediaUrl && !item.mediaUrl.startsWith('http')
						? `${process.env.REACT_APP_BACKEND_URL}${item.mediaUrl}`
						: item.mediaUrl
				})),
				links: data.links
			};
			setMediaData(processedData);
		} catch (err) {
			console.error('Erro ao buscar mídias do contato:', {
				status: err?.response?.status,
				url: err?.config?.url,
				message: err?.message
			});
			toastError(err);
			setMediaData({
				images: [],
				videos: [],
				audios: [],
				documents: [],
				links: []
			});
		} finally {
			setLoadingMedia(false);
		}
	};

	const renderParticipants = () => {
		if (loadingParticipants) {
			return (
				<div className={classes.loadingContainer}>
					<CircularProgress size={40} />
				</div>
			);
		}

		if (participants.length === 0) {
			return (
				<div className={classes.emptyState}>
					<Typography variant="body2" style={{ marginBottom: 8 }}>
						Nenhum participante encontrado
					</Typography>
					<Button
						variant="outlined"
						size="small"
						color="primary"
						onClick={fetchGroupParticipants}
					>
						Tentar novamente
					</Button>
				</div>
			);
		}

		return (
			<List className={classes.participantsList}>
				{participants.map((participant) => (
					<ListItem key={participant.id} className={classes.participantItem} button onClick={() => handleOpenChatWithParticipant(participant)}>
						<ListItemAvatar>
							<Badge
								overlap="circular"
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'right',
								}}
								badgeContent={
									participant.isSuperAdmin ? (
										<PermIdentityIcon className={classes.superAdminIcon} />
									) : participant.isAdmin ? (
										<PermIdentityIcon className={classes.adminIcon} />
									) : null
								}
							>
								<Avatar
									src={participant.profilePicUrl}
									alt={participant.name}
									className={classes.participantAvatar}
								>
									{participant.name?.charAt(0)?.toUpperCase()}
								</Avatar>
							</Badge>
						</ListItemAvatar>
						<ListItemText
							primary={
								<Typography variant="subtitle2" noWrap>
									{participant.name}
								</Typography>
							}
							secondary={
								<Typography variant="caption" color="textSecondary" noWrap>
									{formatSerializedId(participant.number)}
								</Typography>
							}
						/>
						<ListItemSecondaryAction>
							<IconButton edge="end" size="small" aria-label="Conversar" onClick={(e) => { e.stopPropagation(); handleOpenChatWithParticipant(participant); }}>
								<ChatIcon />
							</IconButton>
							{participant.isSuperAdmin && (
								<Chip
									size="small"
									label="Super Admin"
									className={classes.superAdminChip}
									icon={<PermIdentityIcon />}
								/>
							)}
							{participant.isAdmin && !participant.isSuperAdmin && (
								<Chip
									size="small"
									label="Admin"
									className={classes.adminChip}
									icon={<PersonIcon />}
								/>
							)}
						</ListItemSecondaryAction>
					</ListItem>
				))}
			</List>
		);
	};

	// Abrir conversa privada com participante (Telegram / WhatsApp)
	const handleOpenChatWithParticipant = async (participant) => {
		try {
			const isTelegramParticipant =
				participant?.number?.startsWith("tg_") ||
				contact?.channel === "telegram" ||
				ticket?.channel === "telegram";

			let contactId = participant?.contactId || 0;

			if (!contactId) {
				const profileParams = {};
				if (isTelegramParticipant) {
					profileParams.channel = "telegram";
				}
				if (ticket?.whatsappId) {
					profileParams.whatsappId = ticket.whatsappId;
				}
				const profile = await api.get(
					`/contacts/profile/${encodeURIComponent(participant.number)}`,
					{ params: profileParams }
				);
				contactId = profile?.data?.contactId || 0;
			}

			if (!contactId) {
				const safeName =
					participant.name &&
					String(participant.name).trim() !== "," &&
					!/^[,\s.@]+$/.test(String(participant.name).trim())
						? participant.name
						: participant.number;
				const createBody = {
					name: safeName,
					number: participant.number
				};
				if (isTelegramParticipant) {
					createBody.channel = "telegram";
					if (ticket?.whatsappId) {
						createBody.whatsappId = ticket.whatsappId;
					}
				}
				const createResp = await api.post("/contacts", createBody);
				contactId = createResp?.data?.id;
			}

			if (!contactId) {
				toastError("Contato do participante não encontrado");
				return;
			}

			const queueId =
				ticket?.queueId != null && ticket.queueId !== ""
					? ticket.queueId
					: user?.queues?.[0]?.id ?? null;
			const whatsappId = ticket?.whatsappId ?? null;

			try {
				const { data: newTicket } = await api.post("/tickets", {
					contactId,
					userId: user?.id,
					status: "open",
					queueId,
					whatsappId
				});

				if (newTicket.reusedOpenTicket) {
					toast.info("Ticket já aberto — reutilizando.");
				}
				handleTicketCreated(newTicket);
			} catch (err) {
				const status = err.response?.status;
				const data = err.response?.data;

				if (status === 403 && data?.ticket) {
					setOpenTicketInfo({
						user: data.ticket?.user?.name || "Outro usuário",
						queue: data.ticket?.queue?.name || "Sem fila"
					});
					setOpenTicketAlert(true);
					return;
				}

				if (status === 409 && data?.error) {
					try {
						const existing =
							typeof data.error === "string"
								? JSON.parse(data.error)
								: data.error;
						if (existing.userId !== user?.id) {
							setOpenTicketInfo({
								user: existing.user?.name,
								queue: existing.queue?.name
							});
							setOpenTicketAlert(true);
						} else {
							handleTicketCreated(existing);
						}
						return;
					} catch (_) {
						toastError(err);
						return;
					}
				}

				toastError(err);
			}
		} catch (err) {
			console.error("Erro ao abrir conversa com participante:", err);
			toastError(
				err?.response?.data?.message ||
					"Não foi possível iniciar a conversa com este participante"
			);
		}
	};

	// Handler para quando o ticket for criado
  const handleTicketCreated = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket && (ticket.uuid || ticket.id)) {
      const target = ticket.uuid ? `/tickets/${ticket.uuid}` : `/tickets/${ticket.id}`;
      history.push(target);
    }
  };

	// Função para exportar participantes para Excel
	const handleExportParticipants = () => {
		if (participants.length === 0) {
			toast.error("Não há participantes para exportar");
			return;
		}

		try {
			// Preparar dados para exportação
			const exportData = participants.map((participant, index) => ({
				"#": index + 1,
				"Nome": participant.name || "Sem nome",
				"Número": formatSerializedId(participant.number),
				"Role": participant.isSuperAdmin 
					? "Super Admin" 
					: participant.isAdmin 
					? "Admin" 
					: "Participante"
			}));

			// Criar workbook e worksheet
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.json_to_sheet(exportData);

			// Ajustar largura das colunas
			const colWidths = [
				{ wch: 5 },  // #
				{ wch: 30 }, // Nome
				{ wch: 20 }, // Número
				{ wch: 15 }  // Role
			];
			ws['!cols'] = colWidths;

			// Adicionar worksheet ao workbook
			XLSX.utils.book_append_sheet(wb, ws, "Participantes");

			// Gerar nome do arquivo com o nome do grupo e data
			const groupName = contact.name || "Grupo";
			const fileName = `Participantes_${groupName}_${new Date().toISOString().split('T')[0]}.xlsx`;

			// Fazer download do arquivo
			XLSX.writeFile(wb, fileName);

			toast.success(`Arquivo "${fileName}" exportado com sucesso!`);
		} catch (err) {
			console.error("Erro ao exportar participantes:", err);
			toastError("Erro ao exportar participantes");
		}
	};

	const handleContactToggleAcceptAudio = async () => {
		try {
			const contact = await api.put(`/contacts/toggleAcceptAudio/${ticket.contact.id}`);
			setAcceptAudio(contact.data.acceptAudioMessage);
		} catch (err) {
			toastError(err);
		}
	};

	const handleBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: false });
			toast.success("Contato bloqueado");
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(true);
	};

	const handleUnBlockContact = async (contactId) => {
		try {
			await api.put(`/contacts/block/${contactId}`, { active: true });
			toast.success("Contato desbloqueado");
		} catch (err) {
			toastError(err);
		}
		setBlockingContact(false);
	};

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
		// Limpar pesquisa ao trocar de aba
		if (newValue !== 0) {
			clearSearch();
		}
	};

	const renderSearchResults = () => {
		if (searchLoading) {
			return (
				<div className={classes.emptySearchState}>
					<CircularProgress size={20} />
					<Typography variant="caption" style={{ marginLeft: 8 }}>
						Buscando...
					</Typography>
				</div>
			);
		}

		if (searchResults.length === 0 && searchTerm.length >= 2) {
			return (
				<div className={classes.emptySearchState}>
					<Typography variant="caption">
						Nenhuma mensagem encontrada
					</Typography>
				</div>
			);
		}

		return (
			<div className={classes.searchResults}>
				{searchResults.map((message, index) => (
					<div
						key={message.id}
						className={classes.searchResultItem}
						onClick={() => handleGoToMessage(message)}
					>
						<Typography
							className={classes.searchResultText}
							dangerouslySetInnerHTML={{
								__html: highlightSearchTerm(message.body.substring(0, 100) + (message.body.length > 100 ? "..." : ""))
							}}
						/>
						<Typography className={classes.searchResultDate}>
							{new Date(message.createdAt).toLocaleString('pt-BR')}
							{message.fromMe ? " (Você)" : ` (${contact.name})`}
						</Typography>
					</div>
				))}
			</div>
		);
	};

	// Mídia nas abas: só o bloco inline com TabPanel + loadingMedia (sem função renderMediaContent).
	if (loading) return null;

	return (
		<>
		{newTicketModalOpen && (
			<NewTicketModal
				modalOpen={newTicketModalOpen}
				onClose={handleTicketCreated}
				initialContact={participantInitialContact || undefined}
				initialWhatsappId={ticket?.whatsappId}
				initialChannel={ticket?.channel || contact?.channel}
			/>
		)}
		<ShowTicketOpenModal
			isOpen={openTicketAlert}
			handleClose={() => setOpenTicketAlert(false)}
			user={openTicketInfo.user}
			queue={openTicketInfo.queue}
		/>
		<ContactModal
			open={contactModalOpen}
			onClose={() => {
				console.log('[ContactDrawer] Fechando ContactModal');
				setContactModalOpen(false);
			}}
			contactId={contact?.id}
			initialValues={contact}
			onSave={() => {
				console.log('[ContactDrawer] Contato salvo, fechando modal');
				setContactModalOpen(false);
				// Recarregar dados do contato se necessário
				toast.success('Contato atualizado com sucesso!');
			}}
		/>
			<Drawer
				className={classes.drawer}
				variant={isMobile ? "temporary" : "persistent"}
				// variant="persistent"
				anchor="right"
				open={open}
				onClose={isMobile ? handleDrawerClose : undefined}
				PaperProps={{ 
					style: isMobile ? {
						width: "100vw",
						maxWidth: "400px",
						height: "100vh",
						position: "fixed",
						top: 0,
						right: 0,
						zIndex: 9999,
						backgroundColor: theme.palette.background.paper
					} : { 
						position: "absolute", 
						right: 0, 
						top: 0, 
						height: "100%",
						width: drawerWidth,
						zIndex: 1200
					} 
				}}
				ModalProps={isMobile ? {
					style: { position: "fixed", zIndex: 9999 },
					disableEnforceFocus: true,
					disableAutoFocus: true,
					disableRestoreFocus: true,
					hideBackdrop: false,
					disablePortal: false,
					BackdropProps: {
						style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
						onClick: handleDrawerClose
					},
					keepMounted: false,
					// Permitir que elementos filhos (como Dialog) sejam interativos
					disableScrollLock: true
				} : {
					container: document.getElementById("drawer-container"),
					style: { position: "relative", zIndex: 1200 },
				}}
				classes={{
					paper: classes.drawerPaper,
				}}
			>
				<div className={classes.header}>
					<IconButton onClick={handleDrawerClose}>
						<CloseIcon />
					</IconButton>
					{/* <Typography className={classes.headerTitle} variant="subtitle1"> */}
					<Typography style={{ justifySelf: "center" }}>
						{i18n.t("contactDrawer.header")}
					</Typography>
				</div>

				<div className={classes.contentWrapper}>
					{/* Seção de Switch */}
					<Box className={classes.switchContainer}>
						<Typography
							style={{ marginBottom: 0 }}
							variant="subtitle2"
						>
							<Switch
								size="small"
								checked={acceptAudioMessage}
								onChange={() => handleContactToggleAcceptAudio()}
								name="disableBot"
								color="primary"
							/>
							{i18n.t("ticketOptionsMenu.acceptAudioMessage")}
						</Typography>
					</Box>

					{/* Seção do Perfil */}
					<div className={classes.profileSection}>
						<Paper square variant="outlined" className={classes.contactHeader}>
							{/* Avatar redondo e menor - CLICÁVEL */}
							<Avatar
								src={resolveContactAvatar(contact)}
								alt={contact.name}
								className={classes.contactAvatar}
								onClick={handleImageClick}
							>
								{contact.name?.charAt(0)?.toUpperCase()}
							</Avatar>

							<div className={classes.contactCardHeader}>
								{/* Nome completo */}
								<Typography variant="h6" align="center" style={{ marginBottom: 8 }}>
									{contact.name}
								</Typography>

								{/* Número / ID do grupo */}
								<Typography
									style={{
										fontSize: 12,
										textAlign: "center",
										marginBottom: 4,
										color:
											!contact.isGroup &&
											contact.number &&
											contact.number.includes("@") &&
											!contact.number.includes("@g.us")
												? "#f44336"
												: "inherit"
									}}
								>
									{!contact.isGroup &&
										contact.number &&
										contact.number.includes("@") &&
										!contact.number.includes("@g.us") && (
										<span style={{ fontSize: 10, color: "#f44336", display: "block" }}>
											⚠️ Possível erro: Número parece ser LID
										</span>
									)}
									{contact.isGroup ? (
										<>
											<span style={{ fontSize: 10, display: "block", opacity: 0.75 }}>
												ID do grupo
											</span>
											{hideNum && user.profile === "user"
												? (contact.remoteJid || contact.number || "-").slice(0, 8) + "…"
												: formatSerializedId(
														contact.remoteJid || contact.number,
														{ isGroup: true }
												  )}
										</>
									) : hideNum && user.profile === "user" ? (
										formatSerializedId(contact.number, {
											lid: contact.lid,
											channel: contact.channel || ticket?.channel,
										}).slice(0, -6) +
										"**-**" +
										contact.number.slice(-2)
									) : (
										formatSerializedId(contact.number, {
											lid: contact.lid,
											channel: contact.channel || ticket?.channel,
										})
									)}
								</Typography>

								{/* LID — apenas contato 1:1 WhatsApp */}
								{!contact.isGroup &&
									!["instagram", "facebook"].includes(
										String(contact.channel || ticket?.channel || "").toLowerCase()
									) && (
									<Typography style={{ fontSize: 12, textAlign: "center", marginBottom: 4 }}>
										LID: {contact.lid || "—"}
									</Typography>
								)}

								{/* Email */}
								{contact.email && (
									<Typography style={{ color: "primary", fontSize: 12 }} align="center">
										<Link href={`mailto:${contact.email}`}>{contact.email}</Link>
									</Typography>
								)}
							</div>

							{/* Ícones de ação com tooltips */}
							<div className={classes.contactActions}>
								<Tooltip title={i18n.t("contactDrawer.buttons.edit")} arrow>
									<IconButton
										className={`${classes.actionIcon} ${classes.editIcon}`}
										onClick={(e) => {
											console.log('[ContactDrawer] Botão editar clicado');
											e.preventDefault();
											e.stopPropagation();
											setContactModalOpen(true);
										}}
										onTouchStart={(e) => {
											e.stopPropagation();
										}}
										size="small"
										style={{
											pointerEvents: 'auto',
											touchAction: 'manipulation',
											zIndex: 100
										}}
									>
										<CreateIcon />
									</IconButton>
								</Tooltip>

								<Tooltip
									title={i18n.t("contactDrawer.buttons.scheduledMessages")}
									arrow
								>
									<IconButton
										className={`${classes.actionIcon} ${classes.calendarIcon}`}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleOpenSchedulesDialog();
										}}
										size="small"
									>
										<Badge
											badgeContent={contactScheduleCount}
											color="primary"
											invisible={contactScheduleCount === 0}
											overlap="rectangular"
										>
											<CalendarTodayIcon />
										</Badge>
									</IconButton>
								</Tooltip>

								<Tooltip
									title={!contact.active ? "Desbloquear contato" : "Bloquear contato"}
									arrow
								>
									<IconButton
										className={`${classes.actionIcon} ${!contact.active ? classes.unblockIcon : classes.blockIcon}`}
										onClick={() => contact.active
											? handleBlockContact(contact.id)
											: handleUnBlockContact(contact.id)}
										disabled={loading}
										size="small"
									>
										{!contact.active ? <LockOpenIcon /> : <BlockIcon />}
									</IconButton>
								</Tooltip>
							</div>

							{(contact.id && openForm) && <ContactForm initialContact={contact} onCancel={() => setOpenForm(false)} />}
						</Paper>
					</div>

					{/* Campo de Pesquisa */}
					<Box className={classes.searchContainer}>
						<TextField
							className={classes.searchField}
							fullWidth
							size="small"
							variant="outlined"
							placeholder="Pesquisar nas mensagens..."
							value={searchTerm}
							onChange={handleSearchChange}
							onClick={(e) => e.stopPropagation()}
							onFocus={(e) => e.stopPropagation()}
							disabled={!contact?.id}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
								endAdornment: searchTerm && (
									<InputAdornment position="end">
										<IconButton
											size="small"
											onClick={(e) => {
												e.stopPropagation();
												clearSearch();
											}}
											edge="end"
										>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>

						{/* Resultados da Pesquisa */}
						<Collapse in={showSearchResults}>
							<Paper variant="outlined" style={{ marginTop: 8 }}>
								{renderSearchResults()}
							</Paper>
						</Collapse>
					</Box>

					{/* Abas com Ícones */}
					<Tabs
						value={tabValue}
						onChange={handleTabChange}
						variant="scrollable"
						scrollButtons="auto"
						className={classes.tabsContainer}
					>
						<Tab
							className={classes.tabIcon}
							icon={<InfoIcon />}
							aria-label="Informações"
						/>
						<Tab
							className={classes.tabIcon}
							icon={
								<Box display="flex" alignItems="center">
									<ImageIcon />
									{mediaData.images.length > 0 && (
										<Chip size="small" label={mediaData.images.length} className={classes.tabChip} />
									)}
								</Box>
							}
							aria-label="Imagens"
						/>
						<Tab
							className={classes.tabIcon}
							icon={
								<Box display="flex" alignItems="center">
									<VideocamIcon />
									{mediaData.videos.length > 0 && (
										<Chip size="small" label={mediaData.videos.length} className={classes.tabChip} />
									)}
								</Box>
							}
							aria-label="Vídeos"
						/>
						<Tab
							className={classes.tabIcon}
							icon={
								<Box display="flex" alignItems="center">
									<AudiotrackIcon />
									{mediaData.audios.length > 0 && (
										<Chip size="small" label={mediaData.audios.length} className={classes.tabChip} />
									)}
								</Box>
							}
							aria-label="Áudios"
						/>
						<Tab
							className={classes.tabIcon}
							icon={
								<Box display="flex" alignItems="center">
									<InsertDriveFileIcon />
									{mediaData.documents.length > 0 && (
										<Chip size="small" label={mediaData.documents.length} className={classes.tabChip} />
									)}
								</Box>
							}
							aria-label="Documentos"


						/>
						<Tab
							className={classes.tabIcon}
							icon={
								<Box display="flex" alignItems="center">
									<LinkIcon />
									{mediaData.links.length > 0 && (
										<Chip size="small" label={mediaData.links.length} className={classes.tabChip} />
									)}
								</Box>
							}
							aria-label="Links"
						/>
						{contact.isGroup && (
							<Tab
								className={classes.tabIcon}
								icon={
									<Box display="flex" alignItems="center">
										<GroupIcon />
										{participants.length > 0 && (
											<Chip size="small" label={participants.length} className={classes.tabChip} />
										)}
									</Box>
								}
								aria-label="Participantes"
							/>
						)}
					</Tabs>

					{/* Conteúdo Rolável */}
					{/* Conteúdo Rolável */}
					<div className={classes.scrollableContent}>
						{loading ? (
							<ContactDrawerSkeleton classes={classes} />
						) : (
							<>
								<TabPanel value={tabValue} index={0} classes={classes}>
									<Paper 
										square 
										variant="outlined" 
										className={classes.contactDetails} 
										style={{ 
											marginBottom: 8, 
											pointerEvents: 'auto',
											position: 'relative',
											zIndex: 1,
											overflow: 'visible' // Permitir que o menu do Select apareça
										}}
										onClick={(e) => e.stopPropagation()}
									>
										<TagsKanbanContainer ticket={ticket} />
									</Paper>

									<Paper 
										square 
										variant="outlined" 
										className={classes.contactDetails} 
										style={{ 
											pointerEvents: 'auto',
											position: 'relative',
											zIndex: 1
										}}
										onClick={(e) => e.stopPropagation()}
									>
										<Typography variant="subtitle1" style={{ marginBottom: 10 }}>
											{i18n.t("ticketOptionsMenu.appointmentsModal.title")}
										</Typography>
										<ContactNotes ticket={ticket} />
									</Paper>

									<Paper square variant="outlined" className={classes.contactDetails}>
										<Typography variant="subtitle1">
											{i18n.t("contactDrawer.extraInfo")}
										</Typography>
										{contact?.extraInfo?.map(info => (
											<Paper
												key={info.id}
												square
												variant="outlined"
												className={classes.contactExtraInfo}
											>
												<InputLabel>{info.name}</InputLabel>
												<Typography component="div" noWrap style={{ paddingTop: 2 }}>
													<MarkdownWrapper>{info.value}</MarkdownWrapper>
												</Typography>
											</Paper>
										))}
									</Paper>
								</TabPanel>

								{/* TabPanels das Mídias */}
								<TabPanel value={tabValue} index={1} classes={classes}>
									{loadingMedia ? (
										<div className={classes.loadingContainer}>
											<CircularProgress />
										</div>
									) : (
										mediaData.images.length === 0 ? (
											<div className={classes.emptyState}>
												<Typography variant="body2">Nenhuma imagem encontrada</Typography>
											</div>
										) : (
											<Grid container spacing={1} className={classes.mediaGrid}>
												{mediaData.images.map((item, index) => (
													<Grid item xs={4} key={index}>
														<Paper
															className={classes.mediaItem}
															elevation={1}
															onClick={() => window.open(item.mediaUrl, '_blank')}
														>
															<img
																src={item.mediaUrl}
																alt=""
																className={classes.mediaThumbnail}
															/>
														</Paper>
													</Grid>
												))}
											</Grid>
										)
									)}
								</TabPanel>

								<TabPanel value={tabValue} index={2} classes={classes}>
									{loadingMedia ? (
										<div className={classes.loadingContainer}>
											<CircularProgress />
										</div>
									) : (
										mediaData.videos.length === 0 ? (
											<div className={classes.emptyState}>
												<Typography variant="body2">Nenhum vídeo encontrado</Typography>
											</div>
										) : (
											<Grid container spacing={1} className={classes.mediaGrid}>
												{mediaData.videos.map((item, index) => (
													<Grid item xs={4} key={index}>
														<Paper
															className={classes.mediaItem}
															elevation={1}
															onClick={() => window.open(item.mediaUrl, '_blank')}
														>
															<Box display="flex" flexDirection="column" alignItems="center">
																<VideocamIcon className={classes.mediaIcon} />
																<Typography variant="caption" style={{ marginTop: 4 }}>
																	{new Date(item.createdAt).toLocaleDateString('pt-BR')}
																</Typography>
															</Box>
														</Paper>
													</Grid>
												))}
											</Grid>
										)
									)}
								</TabPanel>

								<TabPanel value={tabValue} index={3} classes={classes}>
									{loadingMedia ? (
										<div className={classes.loadingContainer}>
											<CircularProgress />
										</div>
									) : (
										mediaData.audios.length === 0 ? (
											<div className={classes.emptyState}>
												<Typography variant="body2">Nenhum áudio encontrado</Typography>
											</div>
										) : (
											<Grid container spacing={1} className={classes.mediaGrid}>
												{mediaData.audios.map((item, index) => (
													<Grid item xs={4} key={index}>
														<Paper
															className={classes.mediaItem}
															elevation={1}
															onClick={() => window.open(item.mediaUrl, '_blank')}
														>
															<Box display="flex" flexDirection="column" alignItems="center">
																<AudiotrackIcon className={classes.mediaIcon} />
																<Typography variant="caption" style={{ marginTop: 4 }}>
																	{new Date(item.createdAt).toLocaleDateString('pt-BR')}
																</Typography>
															</Box>
														</Paper>
													</Grid>
												))}
											</Grid>
										)
									)}
								</TabPanel>

								<TabPanel value={tabValue} index={4} classes={classes}>
									{loadingMedia ? (
										<div className={classes.loadingContainer}>
											<CircularProgress />
										</div>
									) : (
										mediaData.documents.length === 0 ? (
											<div className={classes.emptyState}>
												<Typography variant="body2">Nenhum documento encontrado</Typography>
											</div>
										) : (
											<List>
												{mediaData.documents.map((doc, index) => (
													<ListItem
														key={index}
														button
														className={classes.linkItem}
														onClick={() => window.open(doc.mediaUrl, '_blank')}
													>
														<ListItemIcon>
															<InsertDriveFileIcon />
														</ListItemIcon>
														<ListItemText
															primary={doc.body || `Documento ${index + 1}`}
															secondary={new Date(doc.createdAt).toLocaleDateString('pt-BR')}
														/>
													</ListItem>
												))}
											</List>
										)
									)}
								</TabPanel>

								<TabPanel value={tabValue} index={5} classes={classes}>
									{loadingMedia ? (
										<div className={classes.loadingContainer}>
											<CircularProgress />
										</div>
									) : (
										mediaData.links.length === 0 ? (
											<div className={classes.emptyState}>
												<Typography variant="body2">Nenhum link encontrado</Typography>
											</div>
										) : (
											<List>
												{mediaData.links.map((link, index) => (
													<ListItem
														key={index}
														button
														className={classes.linkItem}
														onClick={() => window.open(link.url, '_blank')}
													>
														<ListItemIcon>
															<LinkIcon />
														</ListItemIcon>
														<ListItemText
															primary={link.title || link.url}
															secondary={new Date(link.createdAt).toLocaleDateString('pt-BR')}
														/>
													</ListItem>
												))}
											</List>
										)
									)}
								</TabPanel>

							{/* TabPanel dos Participantes - ÍNDICE 6 */}
							{contact.isGroup && (
								<TabPanel value={tabValue} index={6} classes={classes}>
									<div className={classes.exportButtonContainer}>
										<Typography variant="h6" style={{ display: 'flex', alignItems: 'center' }}>
											<GroupIcon style={{ marginRight: 8 }} />
											Participantes do Grupo ({participants.length})
										</Typography>
									<Button
										variant="outlined"
										color="primary"
										size="small"
										startIcon={<GetAppIcon />}
										onClick={handleExportParticipants}
										className={classes.exportButton}
										disabled={participants.length === 0}
									>
										Exportar
									</Button>
									</div>
									{renderParticipants()}
								</TabPanel>
							)}
							</>
						)}
					</div>
				</div>
			</Drawer>

			{/* Modal da Imagem */}
			<Dialog
				open={imageModalOpen}
				onClose={handleImageModalClose}
				className={classes.imageModal}
				maxWidth="md"
				fullWidth
			>
				<DialogContent className={classes.imageModalContent}>
					<img
						src={resolveContactAvatar(contact)}
						alt={contact?.name || "Foto do contato"}
						className={classes.expandedImage}
					/>
				</DialogContent>
			</Dialog>

			{/* Modal Mensagens Agendadas do Contato */}
			<Dialog
				open={schedulesDialogOpen}
				onClose={() => setSchedulesDialogOpen(false)}
				maxWidth="sm"
				fullWidth
				PaperProps={{ style: { maxHeight: "85vh" } }}
			>
				<CardHeader
					title={i18n.t("contactDrawer.scheduledMessages.title")}
					subheader={contact?.name}
					action={
						<IconButton size="small" onClick={() => setSchedulesDialogOpen(false)}>
							<CloseIcon />
						</IconButton>
					}
				/>
				<Box px={2} pt={0} pb={1}>
					<Tabs
						value={schedulesTab}
						onChange={(e, v) => setSchedulesTab(v)}
						indicatorColor="primary"
						textColor="primary"
						variant="fullWidth"
					>
						<Tab label={i18n.t("contactDrawer.scheduledMessages.pending")} />
						<Tab label={i18n.t("contactDrawer.scheduledMessages.sent")} />
					</Tabs>
				</Box>
				<DialogContent dividers style={{ padding: 0, minHeight: 280 }}>
					{loadingSchedules ? (
						<Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
							<CircularProgress />
						</Box>
					) : (
						<Box px={2} pb={2}>
							{schedulesTab === 0 && (
								<List dense>
									{contactSchedules
										.filter(s => ["PENDENTE", "AGUARDANDO_LEMBRETE"].includes(s.status))
										.sort((a, b) => new Date(a.sendAt) - new Date(b.sendAt))
										.map((s) => (
											<ListItem key={s.id} className={classes.linkItem}>
												<ListItemIcon><CalendarTodayIcon color="action" /></ListItemIcon>
												<ListItemText
													primary={
														<Typography variant="body2" noWrap style={{ maxWidth: 260 }}>
															{s.body?.substring(0, 80)}{s.body?.length > 80 ? "…" : ""}
														</Typography>
													}
													secondary={
														<>
															{new Date(s.sendAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
															<Chip size="small" label={s.status} style={{ marginLeft: 8 }} color="primary" variant="outlined" />
														</>
													}
												/>
											</ListItem>
										))}
									{contactSchedules.filter(s => ["PENDENTE", "AGUARDANDO_LEMBRETE"].includes(s.status)).length === 0 && (
										<Typography color="textSecondary" align="center" style={{ padding: 24 }}>
											{i18n.t("contactDrawer.scheduledMessages.noPending")}
										</Typography>
									)}
								</List>
							)}
							{schedulesTab === 1 && (
								<List dense>
									{contactSchedules
										.filter(s => s.status === "ENVIADA")
										.sort((a, b) => new Date(b.sentAt || b.updatedAt) - new Date(a.sentAt || a.updatedAt))
										.map((s) => (
											<ListItem key={s.id} className={classes.linkItem}>
												<ListItemIcon><CalendarTodayIcon color="action" /></ListItemIcon>
												<ListItemText
													primary={
														<Typography variant="body2" noWrap style={{ maxWidth: 260 }}>
															{s.body?.substring(0, 80)}{s.body?.length > 80 ? "…" : ""}
														</Typography>
													}
													secondary={
														<>
															{s.sentAt
																? new Date(s.sentAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
																: new Date(s.updatedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}
															<Chip size="small" label={i18n.t("contactDrawer.scheduledMessages.sentLabel")} style={{ marginLeft: 8 }} color="secondary" />
														</>
													}
												/>
											</ListItem>
										))}
									{contactSchedules.filter(s => s.status === "ENVIADA").length === 0 && (
										<Typography color="textSecondary" align="center" style={{ padding: 24 }}>
											{i18n.t("contactDrawer.scheduledMessages.noSent")}
										</Typography>
									)}
								</List>
							)}
						</Box>
					)}
				</DialogContent>
			</Dialog>
		</>
	);
};

export default ContactDrawer;